import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Loader2, Timer } from "lucide-react";
import {
  MIN_A, MIN_B, SCOPES, DEFAULT_SCOPE, inScope, computeMinuteIndicators, fmtPct,
} from "@/lib/minutesStats";

const fmtD = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const STATUS_LABEL = {
  titular: "Titular", suplente: "Suplente", convocado: "Convocado",
  no_convocado: "No convocado", lesion: "Lesión", no_disponible: "No disponible",
};

function IndicatorBlock({ meta, calc }) {
  return (
    <div className={`rounded-xl border ${meta.border} ${meta.bg} p-3`} data-testid={`breakdown-${meta.key}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className={`text-sm font-black ${meta.text}`}>{meta.long}</div>
        <div className={`text-2xl font-black ${meta.text}`} data-testid={`breakdown-${meta.key}-pct`}>
          {fmtPct(calc.pct)}
        </div>
      </div>
      {calc.pct == null ? (
        <p className="text-xs text-slate-500 italic">Sin partidos con datos en este filtro.</p>
      ) : (
        <>
          <div className="text-[11px] text-slate-600 mb-2">
            Cálculo: <b>{calc.num}'</b> jugados / <b>{calc.den}'</b> disputados × 100 ={" "}
            <b className={meta.text}>{fmtPct(calc.pct)}</b>
          </div>
          <div className="max-h-56 overflow-y-auto rounded-lg border border-white bg-white/70">
            <table className="w-full text-[11px]">
              <thead className="text-slate-400 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-2 py-1">Fecha</th>
                  <th className="text-left px-2 py-1">Rival</th>
                  <th className="text-left px-2 py-1">Situación</th>
                  <th className="text-left px-2 py-1 w-24">Reparto</th>
                  <th className="text-right px-2 py-1">Min.</th>
                  <th className="text-right px-2 py-1">Dur.</th>
                </tr>
              </thead>
              <tbody>
                {calc.games.map((m) => {
                  const pct = m.duration ? Math.min(100, Math.round((m.minutes / m.duration) * 100)) : 0;
                  const played = m.minutes > 0;
                  return (
                    <tr key={m.match_id} className="border-t border-slate-100">
                      <td className="px-2 py-1 whitespace-nowrap">{fmtD(m.date)}</td>
                      <td className="px-2 py-1 truncate max-w-[110px]">{m.rival || "—"}</td>
                      <td className="px-2 py-1">{STATUS_LABEL[m.status] || "—"}</td>
                      <td className="px-2 py-1">
                        <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden"
                             title={played ? `${m.minutes}' de ${m.duration}'` : "Convocado sin jugar"}
                             data-testid={`breakdown-${meta.key}-bar-${m.match_id}`}>
                          <div className={`h-full rounded-full ${played ? "bg-emerald-500" : "bg-slate-300"}`}
                               style={{ width: `${played ? pct : 100}%`, opacity: played ? 1 : 0.5 }} />
                        </div>
                      </td>
                      <td className="px-2 py-1 text-right font-bold">{m.minutes}'</td>
                      <td className="px-2 py-1 text-right text-slate-400">{m.duration}'</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-full bg-emerald-500" /> Jugó</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-full bg-slate-300" /> Convocado sin jugar</span>
          </div>
        </>
      )}
    </div>
  );
}

export default function MinutesBreakdownDialog({ open, onClose, playerId, playerName, season, scope: initialScope, dateFrom, dateTo }) {
  const [scope, setScope] = useState(initialScope || DEFAULT_SCOPE);
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setScope(initialScope || DEFAULT_SCOPE); }, [open, initialScope]);

  useEffect(() => {
    if (!open || !playerId) return;
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get(`/players/${playerId}/matches-detail`, { params })
      .then((r) => setMatches(r.data?.matches || []))
      .catch(() => setMatches([]))
      .finally(() => setLoading(false));
  }, [open, playerId, dateFrom, dateTo]);

  const ind = useMemo(() => computeMinuteIndicators(matches || [], scope), [matches, scope]);
  const pending = useMemo(
    () => (matches || []).filter((m) => inScope(m.season_phase, scope) && m.pending).length,
    [matches, scope],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="minutes-breakdown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Timer className="h-4 w-4 text-purple-600" /> Minutos de {playerName}
            {season ? <span className="text-xs font-normal text-slate-400">· {season}</span> : null}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Filtro</span>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-44 h-8" data-testid="breakdown-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SCOPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>
        ) : (
          <div className="space-y-3">
            <IndicatorBlock meta={MIN_A} calc={ind.total} />
            <IndicatorBlock meta={MIN_B} calc={ind.called} />
            {pending > 0 && (
              <p className="text-[11px] text-amber-600" data-testid="breakdown-pending">
                {pending} partido(s) con convocatoria pendiente de registrar — no cuentan en el cálculo.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
